import React, { useState, useEffect, useCallback } from 'react';
import {
  Brain, Tag, FolderOpen, Plus, Pencil, Trash2, Star, ChevronDown, ChevronRight, X, RefreshCw, AlertCircle, Check,
  MessageSquare, Shield, Search, Upload, BarChart3, Eye, EyeOff,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { knowledgeService } from '../services/knowledgeService';
import type { Category, Tag as TagType, ManagedOntologyItem, OntologyEntityType, OntologyRelationshipType } from '../types/knowledge';

type KBSettingsProps = {
  orgId?: string;
  userId?: string;
  isOrgAdmin?: boolean;
  isPlatformAdmin?: boolean;
};

export const KBSettings: React.FC<KBSettingsProps> = ({ orgId, userId, isOrgAdmin = false, isPlatformAdmin = false }) => {
  const graphEnabled = knowledgeService.isGraphEnabled();
  const [openSection, setOpenSection] = useState<'ontologies' | 'categories' | 'tags' | 'golden'>('categories');

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-800">Knowledge Base Settings</h2>

      {!graphEnabled && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
          Graph features are disabled. Set <code className="bg-amber-100 px-1 rounded">VITE_KB_GRAPH_ENABLED=true</code> and run Docker to enable ontology management and graph pipeline.
        </div>
      )}

      <SectionToggle
        title={`Ontology Manager${!graphEnabled ? ' (requires Docker)' : ''}`}
        icon={<Brain className="w-4 h-4" />}
        isOpen={openSection === 'ontologies'}
        onClick={() => setOpenSection('ontologies')}
      >
        {graphEnabled ? (
          <OntologyManager />
        ) : (
          <p className="text-sm text-gray-500">Ontology management requires graph features to be enabled (Docker + Memgraph).</p>
        )}
      </SectionToggle>

      <SectionToggle
        title="Category Manager"
        icon={<FolderOpen className="w-4 h-4" />}
        isOpen={openSection === 'categories'}
        onClick={() => setOpenSection('categories')}
      >
        <CategoryManager />
      </SectionToggle>

      <SectionToggle
        title="Tag Manager"
        icon={<Tag className="w-4 h-4" />}
        isOpen={openSection === 'tags'}
        onClick={() => setOpenSection('tags')}
      >
        <TagManager />
      </SectionToggle>

      <SectionToggle
        title="Golden Q&A — Verified Answers"
        icon={<Shield className="w-4 h-4" />}
        isOpen={openSection === 'golden'}
        onClick={() => setOpenSection('golden')}
      >
        <GoldenQAManager orgId={orgId} userId={userId} isOrgAdmin={isOrgAdmin} isPlatformAdmin={isPlatformAdmin} />
      </SectionToggle>
    </div>
  );
};

// ─── Section Toggle ──────────────────────────────────────────

const SectionToggle: React.FC<{
  title: string;
  icon: React.ReactNode;
  isOpen: boolean;
  onClick: () => void;
  onToggle?: () => void;
  children: React.ReactNode;
}> = ({ title, icon, isOpen, onClick, children }) => (
  <div className="border border-gray-200 rounded-lg overflow-hidden">
    <button
      onClick={onClick}
      className="w-full flex items-center gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
    >
      {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      <span className="text-purple-600">{icon}</span>
      <span className="text-sm font-medium text-gray-700">{title}</span>
    </button>
    {isOpen && <div className="p-4 border-t border-gray-200">{children}</div>}
  </div>
);

// ─── Ontology Manager ────────────────────────────────────────

const OntologyManager: React.FC = () => {
  const [ontologies, setOntologies] = useState<ManagedOntologyItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const loadOntologies = useCallback(async () => {
    setIsLoading(true);
    try {
      const { ontologies: data } = await knowledgeService.getManagedOntologies();
      setOntologies(data as ManagedOntologyItem[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load ontologies');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadOntologies(); }, [loadOntologies]);

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete ontology "${name}"? This cannot be undone.`)) return;
    try {
      await knowledgeService.deleteOntology(id);
      loadOntologies();
    } catch {
      setError('Failed to delete ontology');
    }
  };

  const handleSetDefault = async (ont: ManagedOntologyItem) => {
    try {
      await knowledgeService.saveOntology({ ...ont, isDefault: true });
      loadOntologies();
    } catch {
      setError('Failed to set default');
    }
  };

  if (isLoading) return <div className="text-sm text-gray-500 flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> Loading ontologies...</div>;
  if (error) return <div className="text-sm text-red-600 flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {error}</div>;

  return (
    <div className="space-y-3">
      {/* Ontology list */}
      {ontologies.map((ont) => (
        <div key={ont.id} className="border border-gray-200 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button onClick={() => setExpandedId(expandedId === ont.id ? null : ont.id)}>
                {expandedId === ont.id ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
              </button>
              <span className="text-sm font-medium text-gray-800">{ont.name}</span>
              {ont.isDefault && (
                <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded-full flex items-center gap-1">
                  <Star className="w-3 h-3" /> Default
                </span>
              )}
              <span className="text-xs text-gray-500">{ont.entityTypeCount} entities, {ont.relationshipTypeCount} rels</span>
              <span className="text-xs text-gray-400">({ont.source})</span>
            </div>
            <div className="flex items-center gap-1">
              {!ont.isDefault && (
                <button onClick={() => handleSetDefault(ont)} className="p-1 text-gray-400 hover:text-yellow-600" title="Set as default">
                  <Star className="w-3.5 h-3.5" />
                </button>
              )}
              <button onClick={() => setEditingId(editingId === ont.id ? null : ont.id)} className="p-1 text-gray-400 hover:text-blue-600" title="Edit">
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => handleDelete(ont.id, ont.name)} className="p-1 text-gray-400 hover:text-red-600" title="Delete">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {ont.description && <p className="text-xs text-gray-500 mt-1 ml-6">{ont.description}</p>}

          {/* Expanded view: entity types + relationships */}
          {expandedId === ont.id && (
            <div className="mt-3 ml-6 space-y-2">
              <div>
                <span className="text-xs font-medium text-purple-700 block mb-1">Entity Types</span>
                <div className="flex flex-wrap gap-1">
                  {ont.entityTypes.map((et) => (
                    <span key={et.name} className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full" title={et.description}>
                      {et.name}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-xs font-medium text-purple-700 block mb-1">Relationships</span>
                <div className="space-y-0.5">
                  {ont.relationshipTypes.map((rt) => (
                    <div key={rt.name} className="text-xs text-gray-600">
                      {rt.fromType} <span className="text-purple-500">→ {rt.name} →</span> {rt.toType}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Inline edit form */}
          {editingId === ont.id && (
            <OntologyEditForm
              ontology={ont}
              onSave={async (updated) => {
                try {
                  await knowledgeService.saveOntology(updated);
                  setEditingId(null);
                  loadOntologies();
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Failed to save ontology');
                }
              }}
              onCancel={() => setEditingId(null)}
            />
          )}
        </div>
      ))}

      {/* Create button */}
      {!showCreate ? (
        <button onClick={() => setShowCreate(true)} className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800">
          <Plus className="w-4 h-4" /> Create New Ontology
        </button>
      ) : (
        <OntologyEditForm
          onSave={async (data) => {
            await knowledgeService.saveOntology(data);
            setShowCreate(false);
            loadOntologies();
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}
    </div>
  );
};

// ─── Ontology Edit Form ──────────────────────────────────────

const OntologyEditForm: React.FC<{
  ontology?: ManagedOntologyItem;
  onSave: (data: { id?: string; domain: string; name: string; description?: string; isDefault?: boolean; entityTypes: OntologyEntityType[]; relationshipTypes: OntologyRelationshipType[] }) => Promise<void>;
  onCancel: () => void;
}> = ({ ontology, onSave, onCancel }) => {
  const [name, setName] = useState(ontology?.name || '');
  const [domain, setDomain] = useState(ontology?.domain || '');
  const [description, setDescription] = useState(ontology?.description || '');
  const [isDefault, setIsDefault] = useState(ontology?.isDefault || false);
  const [entityTypes, setEntityTypes] = useState<OntologyEntityType[]>(ontology?.entityTypes || []);
  const [relationshipTypes, setRelationshipTypes] = useState<OntologyRelationshipType[]>(ontology?.relationshipTypes || []);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave({ id: ontology?.id, domain, name, description, isDefault, entityTypes, relationshipTypes });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-gray-600 block mb-1">Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Concussion Sport" className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600 block mb-1">Domain</label>
          <input value={domain} onChange={(e) => setDomain(e.target.value.toLowerCase().replace(/\s+/g, '_'))} placeholder="concussion_sport" className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" />
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-gray-600 block mb-1">Description</label>
        <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brief description..." className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" />
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} className="rounded border-gray-300 text-purple-600" />
        Set as default ontology
      </label>

      {/* Entity types */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-gray-600">Entity Types ({entityTypes.length})</span>
          <button
            onClick={() => setEntityTypes([...entityTypes, { name: '', description: '', properties: [] }])}
            className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> Add
          </button>
        </div>
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {entityTypes.map((et, idx) => (
            <div key={idx} className="flex items-center gap-2 p-1.5 bg-white rounded border border-gray-100">
              <input
                value={et.name}
                onChange={(e) => {
                  const updated = [...entityTypes];
                  updated[idx] = { ...et, name: e.target.value };
                  setEntityTypes(updated);
                }}
                placeholder="EntityName"
                className="flex-1 text-xs border-none p-0 focus:ring-0 bg-transparent font-medium"
              />
              <input
                value={et.description}
                onChange={(e) => {
                  const updated = [...entityTypes];
                  updated[idx] = { ...et, description: e.target.value };
                  setEntityTypes(updated);
                }}
                placeholder="Description"
                className="flex-2 text-xs border-none p-0 focus:ring-0 bg-transparent text-gray-500"
              />
              <button onClick={() => setEntityTypes(entityTypes.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-600">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Relationship types */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-gray-600">Relationships ({relationshipTypes.length})</span>
          <button
            onClick={() => setRelationshipTypes([...relationshipTypes, { name: '', fromType: '', toType: '', description: '' }])}
            className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> Add
          </button>
        </div>
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {relationshipTypes.map((rt, idx) => (
            <div key={idx} className="flex items-center gap-1 p-1.5 bg-white rounded border border-gray-100 text-xs">
              <input value={rt.fromType} onChange={(e) => { const u = [...relationshipTypes]; u[idx] = { ...rt, fromType: e.target.value }; setRelationshipTypes(u); }} placeholder="From" className="w-24 border-none p-0 focus:ring-0 bg-transparent" />
              <span className="text-purple-400">→</span>
              <input value={rt.name} onChange={(e) => { const u = [...relationshipTypes]; u[idx] = { ...rt, name: e.target.value }; setRelationshipTypes(u); }} placeholder="REL_TYPE" className="w-28 border-none p-0 focus:ring-0 bg-transparent font-medium" />
              <span className="text-purple-400">→</span>
              <input value={rt.toType} onChange={(e) => { const u = [...relationshipTypes]; u[idx] = { ...rt, toType: e.target.value }; setRelationshipTypes(u); }} placeholder="To" className="w-24 border-none p-0 focus:ring-0 bg-transparent" />
              <button onClick={() => setRelationshipTypes(relationshipTypes.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-600">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} className="px-3 py-1.5 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
        <button onClick={handleSave} disabled={isSaving || !name || !domain} className="px-3 py-1.5 text-xs text-white bg-purple-600 rounded hover:bg-purple-700 disabled:opacity-50">
          {isSaving ? 'Saving...' : ontology ? 'Update' : 'Create'}
        </button>
      </div>
    </div>
  );
};

// ─── Category Manager ────────────────────────────────────────

const CategoryManager: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCat, setEditingCat] = useState<Category | null>(null);
  const [formName, setFormName] = useState('');
  const [formDisplayName, setFormDisplayName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formSubcategories, setFormSubcategories] = useState<string[]>([]);
  const [subInput, setSubInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const loadCategories = useCallback(async () => {
    setIsLoading(true);
    try {
      const { categories: data } = await knowledgeService.getCategories();
      setCategories(data);
    } catch {
      setError('Failed to load categories');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const openCreate = () => {
    setEditingCat(null);
    setFormName('');
    setFormDisplayName('');
    setFormDescription('');
    setFormSubcategories([]);
    setSubInput('');
    setShowForm(true);
  };

  const openEdit = (cat: Category) => {
    setEditingCat(cat);
    setFormName(cat.name);
    setFormDisplayName(cat.displayName);
    setFormDescription(cat.description || '');
    setFormSubcategories(cat.subcategories || []);
    setSubInput('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formName || !formDisplayName) return;
    setIsSaving(true);
    setError(null);
    try {
      if (editingCat) {
        await knowledgeService.updateCategory(editingCat.id, {
          displayName: formDisplayName,
          description: formDescription || undefined,
          subcategories: formSubcategories,
        });
      } else {
        await knowledgeService.createCategory({
          name: formName,
          displayName: formDisplayName,
          description: formDescription || undefined,
          subcategories: formSubcategories,
        });
      }
      setShowForm(false);
      setEditingCat(null);
      loadCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save category');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete category "${name}"?`)) return;
    try {
      await knowledgeService.deleteCategory(id);
      loadCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  if (isLoading) return <div className="text-sm text-gray-500 flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> Loading...</div>;

  return (
    <div className="space-y-3">
      {error && <div className="text-xs text-red-600 flex items-center gap-1 mb-2"><AlertCircle className="w-3 h-3" /> {error}</div>}

      {/* Category list */}
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500 border-b">
            <th className="pb-1 font-medium">Name</th>
            <th className="pb-1 font-medium">Subcategories</th>
            <th className="pb-1 font-medium">Description</th>
            <th className="pb-1 font-medium">Scope</th>
            <th className="pb-1 font-medium w-20">Actions</th>
          </tr>
        </thead>
        <tbody>
          {categories.map((cat) => (
            <tr key={cat.id} className="border-b border-gray-50 hover:bg-gray-50">
              <td className="py-2">
                <span className="font-medium text-gray-800">{cat.displayName}</span>
                <span className="text-xs text-gray-400 ml-1">({cat.name})</span>
              </td>
              <td className="py-2">
                <div className="flex flex-wrap gap-1">
                  {(cat.subcategories || []).map((sub: string) => (
                    <span key={sub} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{sub}</span>
                  ))}
                  {(!cat.subcategories || cat.subcategories.length === 0) && <span className="text-xs text-gray-400">—</span>}
                </div>
              </td>
              <td className="py-2 text-gray-500 text-xs">{cat.description || '—'}</td>
              <td className="py-2">
                {cat.isGlobal
                  ? <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">Global</span>
                  : <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full">Org</span>
                }
              </td>
              <td className="py-2">
                <div className="flex items-center gap-1">
                  <button onClick={() => openEdit(cat)} className="p-1 text-gray-400 hover:text-blue-600" title="Edit">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  {!cat.isGlobal && (
                    <button onClick={() => handleDelete(cat.id, cat.displayName)} className="p-1 text-gray-400 hover:text-red-600" title="Delete">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Create / Edit form */}
      {showForm ? (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
          <h4 className="text-sm font-medium text-gray-700">{editingCat ? 'Edit Category' : 'New Category'}</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-600 block mb-1">Display Name</label>
              <input
                value={formDisplayName}
                onChange={(e) => {
                  setFormDisplayName(e.target.value);
                  if (!editingCat) setFormName(e.target.value.toLowerCase().replace(/\s+/g, '_'));
                }}
                placeholder="Sports Medicine"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600 block mb-1">Slug</label>
              <input
                value={formName}
                onChange={(e) => setFormName(e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                placeholder="sports_medicine"
                disabled={!!editingCat}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-500 disabled:bg-gray-100"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-600 block mb-1">Description</label>
            <input
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              placeholder="Brief description of this category..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600 block mb-1">Subcategories</label>
            <div className="flex flex-wrap gap-1 min-h-[38px] p-2 border border-gray-300 rounded-lg bg-white">
              {formSubcategories.map((sub) => (
                <span key={sub} className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">
                  {sub}
                  <button type="button" onClick={() => setFormSubcategories(formSubcategories.filter(s => s !== sub))} className="hover:text-red-600">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <input
                type="text"
                value={subInput}
                onChange={(e) => setSubInput(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ',') && subInput.trim()) {
                    e.preventDefault();
                    const newSub = subInput.trim().toLowerCase().replace(/\s+/g, '-');
                    if (!formSubcategories.includes(newSub)) {
                      setFormSubcategories([...formSubcategories, newSub]);
                    }
                    setSubInput('');
                  }
                  if (e.key === 'Backspace' && !subInput && formSubcategories.length > 0) {
                    setFormSubcategories(formSubcategories.slice(0, -1));
                  }
                }}
                placeholder={formSubcategories.length === 0 ? "Type subcategory + Enter" : ""}
                className="flex-1 min-w-[100px] text-sm border-none p-0 focus:ring-0 bg-transparent outline-none"
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">Press Enter or comma to add. These appear as subcategory options in the upload dialog.</p>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => { setShowForm(false); setEditingCat(null); }} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button onClick={handleSave} disabled={isSaving || !formName || !formDisplayName} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {isSaving ? 'Saving...' : editingCat ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      ) : (
        <button onClick={openCreate} className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800">
          <Plus className="w-4 h-4" /> Add Category
        </button>
      )}
    </div>
  );
};

// ─── Tag Manager ─────────────────────────────────────────────

const TagManager: React.FC = () => {
  const [tags, setTags] = useState<TagType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [newTagInput, setNewTagInput] = useState('');

  const loadTags = useCallback(async () => {
    setIsLoading(true);
    try {
      const { tags: data } = await knowledgeService.getTags();
      setTags(data);
    } catch {
      setError('Failed to load tags');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadTags(); }, [loadTags]);

  const handleRename = async (id: string) => {
    if (!renameValue.trim()) return;
    try {
      await knowledgeService.renameTag(id, renameValue.trim());
      setRenamingId(null);
      loadTags();
    } catch {
      setError('Failed to rename tag');
    }
  };

  const handleDelete = async (id: string, name: string, usageCount: number) => {
    const msg = usageCount > 0
      ? `Delete tag "${name}"? It's used by ${usageCount} document(s). The tag will be removed from those documents.`
      : `Delete tag "${name}"?`;
    if (!window.confirm(msg)) return;
    try {
      await knowledgeService.deleteTag(id);
      loadTags();
    } catch {
      setError('Failed to delete tag');
    }
  };

  if (isLoading) return <div className="text-sm text-gray-500 flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> Loading...</div>;

  return (
    <div className="space-y-2">
      {error && <div className="text-xs text-red-600 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {error}</div>}

      {/* Add tag input */}
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={newTagInput}
          onChange={(e) => setNewTagInput(e.target.value)}
          onKeyDown={async (e) => {
            if (e.key === 'Enter' && newTagInput.trim()) {
              e.preventDefault();
              try {
                await knowledgeService.createTag(newTagInput.trim());
                setNewTagInput('');
                loadTags();
              } catch {
                setError('Failed to create tag');
              }
            }
          }}
          placeholder="Type new tag + Enter"
          className="flex-1 max-w-xs px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
        />
        <button
          onClick={async () => {
            if (!newTagInput.trim()) return;
            try {
              await knowledgeService.createTag(newTagInput.trim());
              setNewTagInput('');
              loadTags();
            } catch {
              setError('Failed to create tag');
            }
          }}
          disabled={!newTagInput.trim()}
          className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {tags.length === 0 ? (
        <p className="text-sm text-gray-500">No tags yet. Add manually above or they are auto-created when documents are uploaded.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <div key={tag.id} className="flex items-center gap-1 bg-blue-50 border border-blue-200 rounded-full px-3 py-1">
              {renamingId === tag.id ? (
                <div className="flex items-center gap-1">
                  <input
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleRename(tag.id)}
                    className="text-xs w-20 border-none p-0 focus:ring-0 bg-transparent"
                    autoFocus
                  />
                  <button onClick={() => handleRename(tag.id)} className="text-green-600"><Check className="w-3 h-3" /></button>
                  <button onClick={() => setRenamingId(null)} className="text-gray-400"><X className="w-3 h-3" /></button>
                </div>
              ) : (
                <>
                  <span className="text-xs font-medium text-blue-700">{tag.name}</span>
                  <span className="text-xs text-blue-400">({tag.usageCount})</span>
                  <button onClick={() => { setRenamingId(tag.id); setRenameValue(tag.name); }} className="text-blue-400 hover:text-blue-600 ml-1">
                    <Pencil className="w-3 h-3" />
                  </button>
                  <button onClick={() => handleDelete(tag.id, tag.name, tag.usageCount)} className="text-blue-400 hover:text-red-600">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Golden Q&A Manager ──────────────────────────────────────

type GoldenEntry = {
  id: string;
  question: string;
  question_variants: string[];
  answer: string;
  sources: Array<{ documentName: string; sectionTitle?: string; pageRange?: string }>;
  category: string | null;
  tags: string[];
  visibility: string;
  confidence: number;
  match_threshold: number;
  is_active: boolean;
  hit_count: number;
  last_hit_at: string | null;
  created_at: string;
};

const GoldenQAManager: React.FC<{ orgId?: string; userId?: string; isOrgAdmin?: boolean; isPlatformAdmin?: boolean }> = ({ orgId, userId, isOrgAdmin = false, isPlatformAdmin = false }) => {
  const [entries, setEntries] = useState<GoldenEntry[]>([]);
  const [statsData, setStatsData] = useState<{ totalEntries: number; activeEntries: number; totalHits: number; topEntries: any[] } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState<GoldenEntry | null>(null);
  const [testQuery, setTestQuery] = useState('');
  const [testResult, setTestResult] = useState<{ results: any[]; tier: string } | null>(null);
  const [isTesting, setIsTesting] = useState(false);

  const loadEntries = useCallback(async () => {
    setIsLoading(true);
    try {
      const { entries: data } = await knowledgeService.getGoldenEntries(orgId, userId);
      setEntries(data);
      const s = await knowledgeService.getGoldenStats(orgId);
      setStatsData(s);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load golden entries');
    } finally {
      setIsLoading(false);
    }
  }, [orgId]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const handleDelete = async (id: string, question: string) => {
    if (!window.confirm(`Deactivate golden answer for "${question.substring(0, 60)}..."?`)) return;
    try {
      await knowledgeService.deleteGoldenEntry(id);
      loadEntries();
    } catch {
      setError('Failed to deactivate entry');
    }
  };

  const handleTestMatch = async () => {
    if (!testQuery.trim()) return;
    setIsTesting(true);
    setTestResult(null);
    try {
      const result = await knowledgeService.testGoldenMatch(testQuery, orgId, userId);
      setTestResult(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Test failed');
    } finally {
      setIsTesting(false);
    }
  };

  if (isLoading) return <div className="text-sm text-gray-500 flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> Loading golden Q&A...</div>;

  return (
    <div className="space-y-4">
      {error && <div className="text-xs text-red-600 flex items-center gap-1 mb-2"><AlertCircle className="w-3 h-3" /> {error} <button onClick={() => setError(null)} className="ml-1 underline">dismiss</button></div>}

      {/* Stats bar */}
      {statsData && (
        <div className="flex items-center gap-4 text-xs text-gray-500 bg-gray-50 rounded-lg px-4 py-2 border border-gray-200">
          <span className="flex items-center gap-1"><BarChart3 className="w-3 h-3" /> {statsData.activeEntries} entries</span>
          <span>{statsData.totalHits} total hits</span>
          {statsData.topEntries.length > 0 && (
            <span className="text-gray-400">Top: "{statsData.topEntries[0]?.question?.substring(0, 40)}..." ({statsData.topEntries[0]?.hit_count} hits)</span>
          )}
        </div>
      )}

      {/* Test Match */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            value={testQuery}
            onChange={(e) => setTestQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleTestMatch()}
            placeholder="Test a query against golden store..."
            className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        <button onClick={handleTestMatch} disabled={isTesting || !testQuery.trim()} className="px-3 py-1.5 text-xs text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50">
          {isTesting ? 'Testing...' : 'Test Match'}
        </button>
      </div>

      {testResult && (
        <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200 text-sm">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-indigo-800">Tier: {testResult.tier}</span>
            {testResult.tier === 'direct' && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Would short-circuit RAG</span>}
            {testResult.tier === 'augment' && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Would augment RAG context</span>}
            {testResult.tier === 'none' && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">No match — normal RAG</span>}
          </div>
          {testResult.results.map((r: any, i: number) => (
            <div key={i} className="text-xs text-gray-600 mt-1">
              #{i + 1} — "{r.question?.substring(0, 60)}..." — similarity: {(r.similarity * 100).toFixed(1)}%
            </div>
          ))}
        </div>
      )}

      {/* Entry list */}
      {entries.length === 0 ? (
        <p className="text-sm text-gray-500">No golden Q&A entries yet. Add verified answers for consistent, fast responses to common questions.</p>
      ) : (
        <div className="space-y-2">
          {entries.map((entry) => (
            <div key={entry.id} className="border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <MessageSquare className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-800 truncate">{entry.question}</span>
                  </div>
                  <div className="text-xs text-gray-500 line-clamp-2 ml-6 kb-markdown">
                    <ReactMarkdown components={{
                      p: ({ children }) => <span>{children} </span>,
                      ul: ({ children }) => <span>{children}</span>,
                      ol: ({ children }) => <span>{children}</span>,
                      li: ({ children }) => <span>• {children} </span>,
                      strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                      blockquote: ({ children }) => <span>{children}</span>,
                    }}>
                      {entry.answer.substring(0, 250)}
                    </ReactMarkdown>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 ml-6 text-xs text-gray-400">
                    {entry.category && <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{entry.category}</span>}
                    <span>{entry.hit_count} hits</span>
                    {entry.last_hit_at && <span>last: {new Date(entry.last_hit_at).toLocaleDateString()}</span>}
                    <span>threshold: {entry.match_threshold}</span>
                    <span className={entry.visibility === 'global' ? 'text-amber-600' : entry.visibility === 'org' ? 'text-blue-600' : 'text-gray-500'}>
                      {entry.visibility}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => { setEditingEntry(entry); setShowForm(true); }} className="p-1 text-gray-400 hover:text-blue-600" title="Edit">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => handleDelete(entry.id, entry.question)} className="p-1 text-gray-400 hover:text-red-600" title="Deactivate">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit form */}
      {showForm ? (
        <GoldenQAForm
          entry={editingEntry}
          orgId={orgId}
          userId={userId}
          isOrgAdmin={isOrgAdmin}
          isPlatformAdmin={isPlatformAdmin}
          onSave={async () => { setShowForm(false); setEditingEntry(null); loadEntries(); }}
          onCancel={() => { setShowForm(false); setEditingEntry(null); }}
        />
      ) : (
        <button onClick={() => { setEditingEntry(null); setShowForm(true); }} className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800">
          <Plus className="w-4 h-4" /> Add Golden Answer
        </button>
      )}
    </div>
  );
};

// ─── Golden Q&A Create/Edit Form ─────────────────────────────

const GoldenQAForm: React.FC<{
  entry?: GoldenEntry | null;
  orgId?: string;
  userId?: string;
  isOrgAdmin?: boolean;
  isPlatformAdmin?: boolean;
  onSave: () => void;
  onCancel: () => void;
}> = ({ entry, orgId, userId, isOrgAdmin = false, isPlatformAdmin = false, onSave, onCancel }) => {
  const [question, setQuestion] = useState(entry?.question || '');
  const [variantsList, setVariantsList] = useState<string[]>(entry?.question_variants || []);
  const [variantInput, setVariantInput] = useState('');
  const [answerText, setAnswerText] = useState(entry?.answer || '');
  const [showPreview, setShowPreview] = useState(false);
  const [formCategory, setFormCategory] = useState(entry?.category || '');
  const [formVisibility, setFormVisibility] = useState<'private' | 'org' | 'global'>(
    (entry?.visibility as 'private' | 'org' | 'global') || 'org'
  );
  const [formConfidence, setFormConfidence] = useState(entry?.confidence ?? 1.0);
  const [formMatchThreshold, setFormMatchThreshold] = useState(entry?.match_threshold ?? 0.90);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!question.trim() || !answerText.trim()) return;
    setIsSaving(true);
    setFormError(null);
    try {
      if (entry) {
        await knowledgeService.updateGoldenEntry(entry.id, {
          question, questionVariants: variantsList, answer: answerText, category: formCategory || undefined,
          visibility: formVisibility, confidence: formConfidence, matchThreshold: formMatchThreshold,
        }, orgId, userId);
      } else {
        await knowledgeService.createGoldenEntry({
          question, questionVariants: variantsList, answer: answerText,
          category: formCategory || undefined, visibility: formVisibility, confidence: formConfidence, matchThreshold: formMatchThreshold,
        }, orgId, userId);
      }
      onSave();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
      <h4 className="text-sm font-medium text-gray-700">{entry ? 'Edit Golden Answer' : 'New Golden Answer'}</h4>
      {formError && <div className="text-xs text-red-600">{formError}</div>}

      <div>
        <label className="text-xs text-gray-600 block mb-1">Question (canonical phrasing)</label>
        <input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="What are the red flag symptoms after a concussion?" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
      </div>

      <div>
        <label className="text-xs text-gray-600 block mb-1">Variants (alternative phrasings, averaged into embedding)</label>
        <div className="flex flex-wrap gap-1 min-h-[38px] p-2 border border-gray-300 rounded-lg bg-white">
          {variantsList.map((v, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full">
              {v}
              <button type="button" onClick={() => setVariantsList(variantsList.filter((_, j) => j !== i))} className="hover:text-red-600"><X className="w-3 h-3" /></button>
            </span>
          ))}
          <input
            type="text"
            value={variantInput}
            onChange={(e) => setVariantInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && variantInput.trim()) {
                e.preventDefault();
                setVariantsList([...variantsList, variantInput.trim()]);
                setVariantInput('');
              }
            }}
            placeholder={variantsList.length === 0 ? "Type variant + Enter" : ""}
            className="flex-1 min-w-[150px] text-sm border-none p-0 focus:ring-0 bg-transparent outline-none"
          />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs text-gray-600">Verified Answer <span className="text-gray-400">(Markdown supported)</span></label>
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
          >
            {showPreview ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {showPreview ? 'Edit' : 'Preview'}
          </button>
        </div>
        {showPreview ? (
          <div className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm min-h-[100px] bg-gray-50 kb-markdown">
            <ReactMarkdown
              components={{
                h1: ({ children }) => <h1 className="text-base font-bold text-gray-800 mt-2 mb-1">{children}</h1>,
                h2: ({ children }) => <h2 className="text-sm font-bold text-gray-800 mt-2 mb-1">{children}</h2>,
                h3: ({ children }) => <h3 className="text-sm font-semibold text-gray-700 mt-2 mb-1">{children}</h3>,
                p: ({ children }) => <p className="mb-2 text-gray-700">{children}</p>,
                ul: ({ children }) => <ul className="list-disc pl-4 mb-2 space-y-0.5">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal pl-4 mb-2 space-y-0.5">{children}</ol>,
                li: ({ children }) => <li className="text-gray-700">{children}</li>,
                strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                blockquote: ({ children }) => <blockquote className="border-l-3 border-amber-400 bg-amber-50 pl-3 py-1 my-2 text-sm">{children}</blockquote>,
              }}
            >
              {answerText || '*No content yet*'}
            </ReactMarkdown>
          </div>
        ) : (
          <textarea
            value={answerText}
            onChange={(e) => setAnswerText(e.target.value)}
            rows={6}
            placeholder="Use **bold**, - bullet lists, > blockquotes for formatting..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-y font-mono"
          />
        )}
      </div>

      <div className="grid grid-cols-4 gap-3">
        <div>
          <label className="text-xs text-gray-600 block mb-1">Category</label>
          <input value={formCategory} onChange={(e) => setFormCategory(e.target.value)} placeholder="concussion" className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" />
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1">Visibility</label>
          <div className="flex items-center gap-3 pt-1">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" name="goldenVisibility" value="private" checked={formVisibility === 'private'} onChange={() => setFormVisibility('private')} className="w-3.5 h-3.5 text-indigo-600" />
              <span className="text-xs text-gray-700">Private</span>
            </label>
            {isOrgAdmin && (
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="radio" name="goldenVisibility" value="org" checked={formVisibility === 'org'} onChange={() => setFormVisibility('org')} className="w-3.5 h-3.5 text-indigo-600" />
                <span className="text-xs text-gray-700">Organisation</span>
              </label>
            )}
            {isPlatformAdmin && (
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="radio" name="goldenVisibility" value="global" checked={formVisibility === 'global'} onChange={() => setFormVisibility('global')} className="w-3.5 h-3.5 text-indigo-600" />
                <span className="text-xs text-gray-700">Global</span>
              </label>
            )}
          </div>
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1">Confidence (0–1)</label>
          <input type="number" value={formConfidence} onChange={(e) => setFormConfidence(parseFloat(e.target.value))} min={0} max={1} step={0.05} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" />
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1">Match threshold</label>
          <input type="number" value={formMatchThreshold} onChange={(e) => setFormMatchThreshold(parseFloat(e.target.value))} min={0.5} max={1} step={0.05} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
        <button onClick={handleSave} disabled={isSaving || !question.trim() || !answerText.trim()} className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50">
          {isSaving ? 'Saving...' : entry ? 'Update' : 'Create'}
        </button>
      </div>
    </div>
  );
};
